<template>
  <s-loader
    v-if="loading"
    class="insert-sample__loader"
  />
  <b-container
    v-else
    fluid
  >
    <b-row>
      <b-col
        cols="12"
        md="6"
        lg="3"
      >
        <label>
          {{ $t('engineering.sample-date') }}*
        </label>
        <s-input-date-picker
          v-model="payload.date"
          :time-zone="timeZoneUTC"
          @input="validateDate(payload.date, 'engineering.sample-date')"
        />
      </b-col>
      <b-col
        cols="12"
        md="6"
        lg="3"
      >
        <label>
          {{ $t('engineering.sample-time') }}*
        </label>
        <s-input-time
          v-model="payload.time"
          step="1"
        />
      </b-col>
    </b-row>
    <b-row
      class="mt-3"
      v-for="accordion in accordions"
      :key="accordion.type"
    >
      <b-col cols="12">
        <accordion-panel
          :id="accordion.type"
          :collapsed.sync="accordion.collapsed"
        >
          <template
            class="pl-3"
            #title
          >
            <label class="mb-0">{{ accordion.name }}</label>
          </template>
          <b-row align-v="end">
            <b-col
              v-for="(field, index) in accordion.inputs"
              :key="index"
              :cols="field.cols"
              :sm="field.sm"
              :lg="field.lg"
              :xl="field.xl"
            >
              <label>
                {{ $t(field.label, field.textParams) }}<span v-if="field.required">*</span>
                <info-badge
                  v-if="field.tooltip"
                  :translation-key="field.tooltip"
                />
              </label>
              <div class="input__height">
                <component
                  v-model="payload[field.model]"
                  v-b-tooltip.hover.bottom="$t(`${field.placeholder}`, {...field})"
                  step="1"
                  :is="field.inputComponent"
                  :type="field.type"
                  :placeholder="$t(`${field.placeholder}`, {...field})"
                  :time-zone="field.timeZone"
                  :options="field.options"
                  :maxlength="getFieldMaxLength(field)"
                  :numeric-options="configureNumericOptions(field)"
                  :input-options="{
                    class: { 'text-right': true },
                    invalid: validationProxy(field) === false,
                    required: true,
                  }"
                  :formatter="decimalFormatter"
                  @lazy-blur-native-proxy="onBlurTrigger(field)"
                  @input="touchProxy(field)"
                />
                <b-form-invalid-feedback id="input-live-feedback">
                  {{ getValueRangeMessage(field) }}
                </b-form-invalid-feedback>
              </div>
            </b-col>
          </b-row>
        </accordion-panel>
      </b-col>
    </b-row>
    <b-container fluid>
      <b-row
        class="no-internal-print"
        align-h="end"
      >
        <b-col
          cols="12"
          sm="6"
          md="12"
          lg="3"
          xl="3"
          class="mt-3"
        >
          <b-button
            block
            variant="secondary"
            @click="cancelAction"
          >
            {{ $t('actions.cancel') }}
          </b-button>
        </b-col>
        <b-col
          cols="12"
          sm="6"
          md="12"
          lg="3"
          xl="3"
          class="mt-3"
        >
          <b-button
            block
            variant="primary"
            :disabled="!canSave"
            @click="actionHandler"
          >
            {{ $t('actions.save') }}
          </b-button>
        </b-col>
      </b-row>
    </b-container>
  </b-container>
</template>
<style lang="sass" scoped>
@import "src/styles/defaults/mobile-first"
.insert-sample
  &__loader
    font-size: 2.5em

.input
  &__height
    min-height: 5em
    +media-min-lg()
      min-height: 6em
</style>
<script>
import { required, minValue, maxValue } from 'vuelidate/lib/validators';
import { ChromatographySample } from 'sigma-mappings';
import tabNames from '@/domain/enums/installation/engineering/chromatography/chromatographyTabs';
import AccordionPanel from '@/components/AccordionPanel';
import InfoBadge from '@/components/icon/InfoBadge';
import SampleService from '@/domain/services/installation/engineering/chromatography/sample';
import LoadingMixin from '@/services/loadingMixin';
import {
  formatter as dateTimeFormatter,
  getInitialDate,
  isLessOrEqual,
  joinISODateAndTimeString,
  normalizeToStartOfDayInUserZone,
  setToStartOfDay,
} from '@/services/dateTime/datetimeService';
import {
  formats,
  timezones,
} from '@/constants/localizer';
import inputGroups
  from '@/domain/data/installation/engineering/chromatography/insertSample/inputGroups';

const minDate = getInitialDate();

function minDateValidator(value) {
  if (value == null) return true;
  const valueNormalized = normalizeToStartOfDayInUserZone(value);
  return isLessOrEqual(minDate, valueNormalized);
}

export default {
  name: 'InsertSample',
  components: {
    AccordionPanel,
    InfoBadge,
  },
  data: () => ({
    inputGroups: null,
    accordions: [],
    payload: new ChromatographySample(),
    timeZoneUTC: timezones.UTC,
  }),
  mixins: [
    LoadingMixin,
  ],
  validations: () => ({
    payload: {
      date: {
        required,
        minValue: minDateValidator,
      },
      analyzeDate: {
        minValue: minDateValidator,
      },
      time: {
        required,
      },
      h2: {
        required,
      },
      cH4: {
        required,
      },
      c2H6: {
        required,
      },
      c2H4: {
        required,
      },
      c2H2: {
        required,
      },
      co: {
        required,
      },
      cO2: {
        required,
      },
      n2: {
        required,
      },
      o2: {
        required,
      },
      defaultAnalyticalDetectionLimit: {
        minValue: minValue(1),
        maxValue: maxValue(1000000),
      },
      accuracyFactor: {
        minValue: minValue(10),
        maxValue: maxValue(1000000),
      },
    },
  }),
  methods: {
    getValueRangeMessage(field) {
      const numberOptions = {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      };
      return this.$t('messages.MA086.message', {
        minValue: this.$n(field.minValue, numberOptions),
        maxValue: this.$n(field.maxValue, numberOptions),
      });
    },
    validateDate(field, fieldLabel) {
      if (!field) return;
      if (!this.$v.payload.date.minValue) {
        this.showAlert(fieldLabel);
      }
    },
    onBlurTrigger(input) {
      const { model, label } = input;
      if (model === 'analyzeDate' && !this.$v.payload.analyzeDate.minValue) {
        this.showAlert(label);
      }
    },
    showAlert(field) {
      this.$pushAlert({
        code: 'MA038',
        messageParams: {
          NomeDoCampo: this.$t(field),
          FaixaInicial: dateTimeFormatter(minDate),
        },
      });
    },
    configureNumericOptions(field) {
      return {
        decimalPlaces: field.precision,
        minimumValue: field.minValue,
        maximumValue: field.maxValue,
      };
    },
    handleUndefinedFields() {
      if (!this.payload.defaultAnalyticalDetectionLimit) {
        this.payload.defaultAnalyticalDetectionLimit = 1;
      }
      if (!this.payload.accuracyFactor) {
        this.payload.accuracyFactor = 10;
      }
    },
    saveAnalysis() {
      const { assetModuleId } = this.$route.params;
      this.handleUndefinedFields();
      this.joinDateTimeValuesInUTC();
      SampleService.insert(assetModuleId, this.payload).then(() => {
        this.confirmationToast();
        this.goToGeneralTab();
      });
    },
    editAnalysis() {
      const { assetModuleId } = this.$route.params;
      const { sampleId } = this.$route.query;
      this.joinDateTimeValuesInUTC();
      SampleService.update(assetModuleId, sampleId, this.payload)
        .then(() => {
          this.confirmationToast();
          this.goToGeneralTab();
        });
    },
    cancelAction() {
      this.$confirmMessage({
        code: 'MC006',
      }).accepted(() => {
        this.goToGeneralTab();
      });
    },
    confirmationToast() {
      this.$emit('form-update', false);
      this.$pushAlert({ code: 'MS021' });
    },
    setInputGroups() {
      this.inputGroups = inputGroups;
    },
    decimalFormatter(value) {
      if (typeof value !== 'number') {
        return value;
      }
      return this.$n(value, {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      });
    },
    blockTabChange(block) {
      this.$emit('cant-leave-changed', block);
    },
    goToGeneralTab() {
      this.$router.push({
        name: 'monitoring.engineering.module',
        params: this.$route.params,
        query: {
          tab: tabNames.GENERAL,
        },
      });
    },
    rangeValidations({
      validation,
      minValue: fieldMinValue,
      maxValue: fieldMaxValue,
      model,
    }) {
      const valueProxy = this.payload[model];
      if (valueProxy === null || valueProxy === undefined || !validation) {
        return null;
      }
      return (valueProxy >= fieldMinValue) && (valueProxy <= fieldMaxValue);
    },
    validationProxy(value) {
      const { model } = value;
      if ((this.$v.payload[model] || {}).$error) return false;
      return this.rangeValidations(value) === true ? null : this.rangeValidations(value);
    },
    touchProxy({ model }) {
      if (this.$v.payload[model]) {
        this.$v.payload[model].$touch();
      }
    },
    getAnalysis() {
      const { assetModuleId } = this.$route.params;
      const { sampleId } = this.$route.query;
      this.startLoading();
      SampleService.get(assetModuleId, sampleId)
        .then((data) => {
          this.payload = data;
          this.sliptDateTimeValuesInUTC();
        })
        .finally(() => this.endLoading());
    },
    joinDateTimeValuesInUTC() {
      const {
        date,
        time,
        analyzeDate,
        analyzeTime,
      } = this.payload;
      const opts = { timeZone: this.timeZoneUTC };
      this.payload = {
        ...this.payload,
        sampleDateTime: joinISODateAndTimeString(
          date,
          time,
          opts,
        ),
        analysisDateTime: joinISODateAndTimeString(
          analyzeDate,
          analyzeTime,
          opts,
        ),
      };
    },
    sliptDateTimeValuesInUTC() {
      const {
        analysisDateTime,
        sampleDateTime,
      } = this.payload;
      const opts = { timeZone: this.timeZoneUTC };
      this.payload = {
        ...this.payload,
        date: setToStartOfDay(sampleDateTime, opts),
        time: dateTimeFormatter(sampleDateTime, formats.ISO_TIME, this.timeZoneUTC),
        analyzeDate: setToStartOfDay(analysisDateTime, opts),
        analyzeTime: dateTimeFormatter(analysisDateTime, formats.ISO_TIME, this.timeZoneUTC),
      };
    },
    getFieldMaxLength(field) {
      return field.maxLength || (field.inputOptions && field.inputOptions.maxLength);
    },
    onConfirmAction() {
      if (this.$route.query.sampleId) {
        this.editAnalysis();
        return;
      }
      this.saveAnalysis();
    },
    actionHandler() {
      this.$confirmMessage({ code: 'MC011' }).accepted(() => {
        this.onConfirmAction();
      });
    },
  },
  created() {
    this.setInputGroups();
  },
  computed: {
    fields() {
      return inputGroups.fields(this);
    },
    canSave() {
      return !this.$v.payload.$invalid;
    },
    emptyFormValidator() {
      if (!this.payload) return null;
      return Object.values(this.payload).some(item => item);
    },
  },
  mounted() {
    const { sampleId } = this.$route.query;
    if (sampleId) {
      this.getAnalysis();
    }
    this.accordions = [
      {
        type: 'primary-fields',
        name: this.$t('engineering.chromatography.main-values'),
        collapsed: false,
        inputs: this.fields.primaryFields,
      },
      {
        type: 'secondary-fields',
        name: this.$t('engineering.chromatography.complementary-values'),
        collapsed: true,
        inputs: this.fields.secondaryFields,
      },
      {
        type: 'extra-fields',
        name: this.$t('engineering.chromatography.additional-information'),
        collapsed: true,
        inputs: this.fields.extraFields,
      },
    ];
  },
  watch: {
    emptyFormValidator(newValue) {
      this.blockTabChange(newValue);
    },
  },
};
</script>
